import {
  Criterion,
  CriterionListItem,
  Field,
  FieldChoice,
  FieldType,
  ListValueType,
} from 'queries';
import { v4 as uuidv4 } from 'uuid';
import { KeywordCriterionHandler } from './KeywordCriterionHandler';
import { RangeCriterionHandler } from './RangeCriterionHandler';
import { ChoiceAllowance, ICriterionHandler } from './ICriterionHandler';
import { ParticipantField, SelectionBehavior } from '@app/api';
import { LocaleCriterionHandler } from './LocaleCriterionHandler';
import { TextCriterionHandler } from 'lib/criteria/TextCriterionHandler';
import { LocationCriterionHandler } from './LocationCriterionHandler';

const handlers = [
  new KeywordCriterionHandler(),
  new RangeCriterionHandler(),
  new LocaleCriterionHandler(),
  new TextCriterionHandler(),
  new LocationCriterionHandler(),
];

const getHandler = (criterion: Criterion): ICriterionHandler | undefined => {
  return handlers.find((h) => h.handleCriterion(criterion));
};

export const criterionHandleOthers = (criterion?: Criterion) => {
  if (!criterion) {
    return true;
  }
  const handler = getHandler(criterion);
  if (!handler) {
    return true;
  }
  return handler.criterionHandleOthers();
};

const getChoiceAllowance = (
  criterion: Criterion | undefined,
  choice: FieldChoice,
) => {
  if (!criterion) {
    return ChoiceAllowance.ACCEPTED;
  }
  const handler = getHandler(criterion);
  if (!handler) {
    return ChoiceAllowance.ACCEPTED;
  }
  return handler.getChoiceAllowance(criterion, choice);
};

export const isCriterionDeclineChoice = (
  criterion: Criterion | undefined,
  choice: FieldChoice,
  tolerateAsDeclined?: boolean,
) => {
  const allowance = getChoiceAllowance(criterion, choice);
  return (
    allowance === ChoiceAllowance.DECLINED ||
    (!!tolerateAsDeclined && allowance === ChoiceAllowance.TOLERATED)
  );
};

export const isCriterionTolerateChoice = (
  criterion: Criterion | undefined,
  choice: FieldChoice,
) => getChoiceAllowance(criterion, choice) === ChoiceAllowance.TOLERATED;

export const isCriterionAcceptChoice = (
  criterion: Criterion | undefined,
  choice: FieldChoice,
) =>
  getChoiceAllowance(criterion, choice) ===
  ChoiceAllowance.AUTOMATICALLY_ACCEPTED;

export const toggleDeclinedChoiceInCriterion = (
  criterion: Criterion,
  choice: FieldChoice,
  tolerateAsDeclined?: boolean,
) => {
  const handler = getHandler(criterion);
  if (!handler) {
    return criterion;
  }
  const isDeclined = isCriterionDeclineChoice(
    criterion,
    choice,
    tolerateAsDeclined,
  );

  if (isDeclined) {
    return handler.acceptChoice(criterion, choice);
  }
  return handler.declineChoice(criterion, choice);
};

export const toggleTolerateChoiceInCriterion = (
  criterion: Criterion,
  choice: FieldChoice,
) => {
  const handler = getHandler(criterion);
  if (!handler) {
    return criterion;
  }
  const isTolerate = isCriterionTolerateChoice(criterion, choice);

  if (isTolerate) {
    return handler.acceptChoice(criterion, choice);
  }
  return handler.tolerateChoice(criterion, choice);
};

export const toggleAcceptedChoiceInCriterion = (
  criterion: Criterion,
  choice: FieldChoice,
) => {
  const handler = getHandler(criterion);
  if (!handler) {
    return criterion;
  }

  const isAccepted = isCriterionAcceptChoice(criterion, choice);
  if (isAccepted) {
    return handler.acceptChoice(criterion, choice);
  }
  return handler.automaticallyAcceptChoice(criterion, choice);
};

export const acceptChoice = (criterion: Criterion, choice: FieldChoice) => {
  const handler = getHandler(criterion);
  if (!handler) {
    return criterion;
  }

  return handler.acceptChoice(criterion, choice);
};

export const removeChoice = (criterion: Criterion, choice: FieldChoice) => {
  const handler = getHandler(criterion);
  if (!handler) {
    return criterion;
  }

  return handler.removeChoice(criterion, choice);
};

export const createCriterionForField = (
  field: Field,
  automaticlyAccept: boolean = false,
): Criterion => {
  if (
    field.type !== FieldType.SINGLE_CHOICE &&
    field.type !== FieldType.MULTIPLE_CHOICE
  ) {
    throw new Error('Not Handled');
  }

  const choices: string[] = (field.properties.choices || []).map(
    (choice) => choice.ref,
  );

  return {
    id: uuidv4(),
    fieldRef: field.ref,
    screenerCriterion: true,
    selectionBehavior: SelectionBehavior.OPTIONAL,

    allowList: choices,
    automaticlyAcceptedList: automaticlyAccept ? [...choices] : undefined,
    listValueType: ListValueType.KEYWORD,
    denyList: [],
    quotas: null,
  };
};

export const setCriterionValues = (
  criterion: Criterion,
  values?: CriterionListItem[],
  choices?: FieldChoice[],
): Criterion => {
  const handler = getHandler(criterion);
  if (!handler) {
    throw Error('Not Handled');
  }
  return handler.setCriterionValues(criterion, values, choices);
};

export const createEmptyCriterionFromParticipantField = (
  field: ParticipantField,
): Criterion => {
  const handler = handlers.find((h) => h.handleParticipantField(field));
  if (!handler) {
    throw Error('Not Handled');
  }
  return handler.createEmptyCriterionFromParticipantField(field);
};
